import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type DeepModeBannerState = 'triggered' | 'error' | 'cancelled';

/** API-driven content overrides for a specific banner state */
export interface DeepModeBannerContentOverrides {
  heading?: string;
  subtext?: string;
  footer?: string;
}

export interface DeepModeChatState {
  /** Banner is currently visible (streaming in progress) */
  isActive: boolean;
  /** Streaming has finished — show green dot in sidebar */
  isComplete: boolean;
  /** User has opened the chat and seen the response */
  hasBeenViewed: boolean;
  /** Chat question text for the "Answer Ready" tooltip */
  question: string;
  /** Current visual state of the banner */
  bannerState: DeepModeBannerState;
  /** API-driven heading text from the Complexity_Indicator message (triggered state) */
  deepModeContent?: string;
  /** API-driven content overrides keyed by banner state (error / cancelled) */
  bannerContentOverrides?: Partial<Record<DeepModeBannerState, DeepModeBannerContentOverrides>>;
  /** Monotonic id per deep-mode invocation; increments on each new prompt */
  sessionId: number;
  /** True once the initial banner show delay has elapsed for the current session */
  bannerShowDelayElapsed?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Timer context — isolated so per-second ticks only re-render banner consumers
// ─────────────────────────────────────────────────────────────────────────────

const DeepModeTimerContext = createContext<Record<string, number>>({});

/**
 * Returns the elapsed seconds for a given chatId.
 * Only components that call this hook re-render on each tick.
 */
export const useDeepModeElapsed = (chatId: string): number => {
  const elapsed = useContext(DeepModeTimerContext);
  return elapsed[chatId] ?? 0;
};

interface DeepModeContextType {
  /** Map of chatId → per-chat deep mode state */
  deepModeChats: Record<string, DeepModeChatState>;

  /**
   * Call this when streaming begins and the API response indicates isDeepMode=true.
   *
   * API INTEGRATION POINT:
   *   In `chatResponse.service.tsx → generateSingleResponseObject()`, detect
   *   a message with source === 'Complexity_Classifier' and type === 'Complexity_Indicator'.
   *   Then call: activateDeepMode(chatId, questionText, deepModeContent)
   */
  activateDeepMode: (chatId: string, question: string, deepModeContent?: string) => void;

  /**
   * Call this when streaming ends successfully.
   *   In `Chat.tsx → setLoadingMessage()`, when the loading message is removed
   *   (i.e., newMessages is empty) and the chat was in deep mode, call:
   *   completeDeepMode(chatId)
   */
  completeDeepMode: (chatId: string) => void;

  /**
   * Call this when the user navigates to the chat and views the deep mode response.
   *   In `NavMain.tsx → navigateOnClick()`, after selecting a thread, call:
   *   markChatViewed(chatId)
   */
  markChatViewed: (chatId: string) => void;

  /**
   * Call this on streaming error or user cancellation.
   *   In `Chat.tsx`, on stream error, call: cancelDeepMode(chatId)
   *   Pass contentOverrides to surface API-driven text for heading/subtext/footer.
   */
  cancelDeepMode: (chatId: string, reason?: 'error' | 'cancelled', contentOverrides?: DeepModeBannerContentOverrides) => void;

  /**
   * Updates the banner state mid-stream from an API signal (e.g. error recovery).
   * Also merges any API-driven content overrides for the new state.
   *   In `chatResponse.service.tsx`, call when a Complexity_Indicator with
   *   mode === 'error' or mode === 'cancelled' is detected during streaming.
   *
   * NOTE: Only call with 'error' or 'cancelled'. Passing 'triggered' here
   * after activation would reset the banner to the initial state unintentionally;
   * the 'triggered' state is set exclusively by `activateDeepMode`.
   */
  updateDeepModeBannerState: (chatId: string, state: DeepModeBannerState, contentOverrides?: DeepModeBannerContentOverrides) => void;

  /** Returns true if a given chatId has a completed (unviewed) deep mode response */
  hasUnviewedDeepModeResponse: (chatId: string) => boolean;

  /** Resets deep mode state completely for a given chatId (e.g. when starting a new interaction) */
  resetDeepMode: (chatId: string) => void;

  /**
   * Starts the elapsed-time counter when the Deep Mode banner becomes visible.
   * Resets to zero when `sessionId` changes (new prompt). Idempotent for the
   * same session (e.g. thread switch while the banner stays mounted).
   */
  startBannerTimer: (chatId: string, sessionId: number) => void;

  /**
   * Marks the initial banner show delay as complete for the given session.
   * Persists across thread switches so returning to an in-progress deep mode
   * chat does not re-wait the full delay.
   */
  markBannerShowDelayComplete: (chatId: string, sessionId: number) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const DeepModeContext = createContext<DeepModeContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export const DeepModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [deepModeChats, setDeepModeChats] = useState<Record<string, DeepModeChatState>>({});
  const [elapsedMap, setElapsedMap] = useState<Record<string, number>>({});

  // Track interval refs so we can clear timers per chatId
  const timersRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const nextSessionIdRef = useRef(1);
  const bannerTimerSessionRef = useRef<Record<string, number>>({});
  const deepModeChatsRef = useRef(deepModeChats);
  deepModeChatsRef.current = deepModeChats;
  const pendingSessionRef = useRef<{ chatId: string; sessionId: number } | null>(null);

  const takeSessionIdForChat = useCallback((chatId: string) => {
    const pending = pendingSessionRef.current;
    if (pending?.chatId === chatId) {
      return pending.sessionId;
    }
    const sessionId = nextSessionIdRef.current++;
    pendingSessionRef.current = { chatId, sessionId };
    return sessionId;
  }, []);

  const startTimer = useCallback((chatId: string) => {
    // Clear any existing timer for this chat
    if (timersRef.current[chatId]) {
      clearInterval(timersRef.current[chatId]);
    }
    timersRef.current[chatId] = setInterval(() => {
      setElapsedMap(prev => ({ ...prev, [chatId]: (prev[chatId] ?? 0) + 1 }));
    }, 1000);
  }, []);

  const stopTimer = useCallback((chatId: string) => {
    if (timersRef.current[chatId]) {
      clearInterval(timersRef.current[chatId]);
      delete timersRef.current[chatId];
    }
  }, []);

  const resetBannerTimerState = useCallback((chatId: string) => {
    stopTimer(chatId);
    delete bannerTimerSessionRef.current[chatId];
    setElapsedMap(prev => ({ ...prev, [chatId]: 0 }));
  }, [stopTimer]);

  const startBannerTimer = useCallback((chatId: string, sessionId: number) => {
    if (bannerTimerSessionRef.current[chatId] === sessionId && timersRef.current[chatId]) {
      return;
    }
    bannerTimerSessionRef.current[chatId] = sessionId;
    stopTimer(chatId);
    setElapsedMap(prev => ({ ...prev, [chatId]: 0 }));
    startTimer(chatId);
  }, [startTimer, stopTimer]);

  const markBannerShowDelayComplete = useCallback((chatId: string, sessionId: number) => {
    setDeepModeChats(prev => {
      const chat = prev[chatId];
      if (!chat || chat.sessionId !== sessionId || chat.bannerShowDelayElapsed) return prev;
      return {
        ...prev,
        [chatId]: { ...chat, bannerShowDelayElapsed: true },
      };
    });
  }, []);

  const activateDeepMode = useCallback((chatId: string, question: string, deepModeContent?: string) => {
    let isNewSession = false;

    setDeepModeChats(prev => {
      const current = prev[chatId];
      if (current?.isActive) {
        return {
          ...prev,
          [chatId]: {
            ...current,
            question: question || current.question,
            deepModeContent: deepModeContent ?? current.deepModeContent,
          },
        };
      }
      isNewSession = true;
      return {
        ...prev,
        [chatId]: {
          isActive: true,
          isComplete: false,
          hasBeenViewed: false,
          question,
          bannerState: 'triggered',
          deepModeContent,
          sessionId: takeSessionIdForChat(chatId),
        },
      };
    });

    pendingSessionRef.current = null;

    if (isNewSession) {
      resetBannerTimerState(chatId);
    }
  }, [takeSessionIdForChat, resetBannerTimerState]);

  const completeDeepMode = useCallback((chatId: string) => {
    stopTimer(chatId);
    setDeepModeChats(prev => {
      const chat = prev[chatId];
      if (!chat || !chat.isActive) return prev;
      return {
        ...prev,
        [chatId]: {
          ...chat,
          isActive: false,
          isComplete: true,
        },
      };
    });
  }, [stopTimer]);

  const markChatViewed = useCallback((chatId: string) => {
    setDeepModeChats(prev => {
      const chat = prev[chatId];
      if (!chat) return prev;
      if (chat.isActive) return prev;
      return {
        ...prev,
        [chatId]: {
          ...chat,
          hasBeenViewed: true,
        },
      };
    });
  }, []);

  const cancelDeepMode = useCallback((chatId: string, reason: 'error' | 'cancelled' = 'cancelled', contentOverrides?: DeepModeBannerContentOverrides) => {
    stopTimer(chatId);
    const chat = deepModeChatsRef.current[chatId];
    const newState: DeepModeBannerState = reason === 'error' ? 'error' : 'cancelled';
    const sessionId = chat?.sessionId ?? takeSessionIdForChat(chatId);

    setDeepModeChats(prev => {
      const prevChat = prev[chatId];
      return {
        ...prev,
        [chatId]: {
          isActive: false,
          isComplete: false,
          hasBeenViewed: false,
          question: prevChat?.question || '',
          bannerState: newState,
          deepModeContent: prevChat?.deepModeContent,
          sessionId,
          bannerContentOverrides: contentOverrides
            ? { ...(prevChat?.bannerContentOverrides || {}), [newState]: contentOverrides }
            : prevChat?.bannerContentOverrides,
        },
      };
    });

    pendingSessionRef.current = null;
  }, [stopTimer, takeSessionIdForChat]);

  const updateDeepModeBannerState = useCallback((chatId: string, state: DeepModeBannerState, contentOverrides?: DeepModeBannerContentOverrides) => {
    setDeepModeChats(prev => {
      const chat = prev[chatId];
      if (!chat) {
        return {
          ...prev,
          [chatId]: {
            isActive: false,
            isComplete: false,
            hasBeenViewed: false,
            question: '',
            bannerState: state,
            sessionId: takeSessionIdForChat(chatId),
            bannerContentOverrides: contentOverrides
              ? { [state]: contentOverrides }
              : undefined,
          },
        };
      }
      return {
        ...prev,
        [chatId]: {
          ...chat,
          bannerState: state,
          bannerContentOverrides: contentOverrides
            ? { ...(chat.bannerContentOverrides || {}), [state]: contentOverrides }
            : chat.bannerContentOverrides,
        },
      };
    });

    pendingSessionRef.current = null;
  }, [takeSessionIdForChat]);

  const hasUnviewedDeepModeResponse = useCallback((chatId: string) => {
    const chat = deepModeChats[chatId];
    return !!chat?.isComplete && !chat?.hasBeenViewed;
  }, [deepModeChats]);

  const resetDeepMode = useCallback((chatId: string) => {
    resetBannerTimerState(chatId);
    setDeepModeChats(prev => {
      const updated = { ...prev };
      delete updated[chatId];
      return updated;
    });
    setElapsedMap(prev => {
      const updated = { ...prev };
      delete updated[chatId];
      return updated;
    });
  }, [resetBannerTimerState]);

  // Clear all active timers when the provider unmounts
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearInterval);
    };
  }, []);

  const contextValue = useMemo(() => ({
    deepModeChats,
    activateDeepMode,
    completeDeepMode,
    markChatViewed,
    cancelDeepMode,
    updateDeepModeBannerState,
    hasUnviewedDeepModeResponse,
    resetDeepMode,
    startBannerTimer,
    markBannerShowDelayComplete,
  }), [
    deepModeChats,
    activateDeepMode,
    completeDeepMode,
    markChatViewed,
    cancelDeepMode,
    updateDeepModeBannerState,
    hasUnviewedDeepModeResponse,
    resetDeepMode,
    startBannerTimer,
    markBannerShowDelayComplete,
  ]);

  return (
    <DeepModeTimerContext.Provider value={elapsedMap}>
      <DeepModeContext.Provider value={contextValue}>
        {children}
      </DeepModeContext.Provider>
    </DeepModeTimerContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export const useDeepMode = (): DeepModeContextType => {
  const context = useContext(DeepModeContext);
  if (context === undefined) {
    throw new Error('useDeepMode must be used within a DeepModeProvider');
  }
  return context;
};
